import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { AcceptHigherMultiplerSwitch } from '@/components/AcceptHigherMultiplierSwitch';
import { AcceptMultiplierChangeSwitch } from '@/components/AcceptMultiplierChangeSwitch';
import { Button } from '@/components/ButtonComponent';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { useIsPredictionsProduct } from '@/hooks/use-is-predictions-product';
import { useJurisdictionStore } from '@/hooks/use-jurisdiction';

type OddsChangeFooterProps = {
    onAccept: () => void;
    onClose: () => void;
};

export const OddsChangeFooter = ({ onAccept, onClose }: OddsChangeFooterProps) => {
    const { t } = useTranslation(['betslip_sbk', 'common']);
    const isPredictions = useIsPredictionsProduct();
    const enabledHigherMultipliers = useJurisdictionStore(
        state => state.jurisdictionSettings?.productConfig?.settings?.higher_multiplier_toggle_enabled
    );

    const switches = useMemo(() => {
        const items = [
            {
                label: t(isPredictions ? 'always_accept_price_changes' : 'always_accept_multiplier_changes'),
                component: <AcceptMultiplierChangeSwitch />,
            },
        ];
        if (enabledHigherMultipliers) {
            items.push({
                label: t(isPredictions ? 'always_accept_higher_prices' : 'always_accept_higher_multipliers'),
                component: <AcceptHigherMultiplerSwitch />,
            });
        }
        return items;
    }, [t, isPredictions, enabledHigherMultipliers]);

    return (
        <Box px="s16" pt="s8">
            {switches.map(({ label, component }) => (
                <Box
                    key={label}
                    justifyContent="space-between"
                    alignItems="center"
                    flexDirection="row"
                    paddingVertical="s16"
                >
                    <Text variant="bodyLarge" color={ds.colorToken.text.primary}>
                        {label}
                    </Text>
                    {component}
                </Box>
            ))}
            <Box gap="s12" pt="s8" pb="s16">
                <Button
                    label={t(isPredictions ? 'accept_price_changes' : 'accept_multiplier_changes')}
                    hierarchy="primary"
                    onPress={onAccept}
                />
                <Button label={t('common:close')} hierarchy="secondary" onPress={onClose} />
            </Box>
        </Box>
    );
};
